import React from "react";
import { Box, Typography, Container, Button } from "@mui/material";

const YouTubeSearchResultsExportContent = () => {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <Container maxWidth="md" sx={{ textAlign: "center", mt: 4 }}>
      {/* Headline Section */}
      <Typography
        variant="h4"
        fontWeight="bold"
        sx={{ fontSize: "1.75rem" }}
        gutterBottom
      >
        Extract & Export YouTube Search Results Instantly
      </Typography>
      <Typography variant="h5" fontWeight="bold" gutterBottom>
        Save, Analyze, and Optimize Search Data in Just One Click
      </Typography>

      {/* Problem/Solution */}
      <Typography variant="body1" paragraph>
        Manually copying YouTube search results is time-consuming and
        frustrating. Whether you're doing competitor research, trend analysis,
        or content planning – collecting results should be effortless.
      </Typography>
      <Typography
        variant="body1"
        paragraph
        sx={{ fontWeight: "bold", color: "primary.main" }}
      >
        Our YouTube Search Results Export tool lets you instantly extract search
        data into a structured format – CSV, Excel, or JSON – so you can focus
        on insights, not copy-pasting.
      </Typography>

      {/* Benefits */}
      <Typography variant="h5" fontWeight="bold" gutterBottom mt={3}>
        Why Export YouTube Search Results?
      </Typography>
      <ul
        style={{
          textAlign: "left",
          display: "inline-block",
          listStyleType: "none",
          paddingLeft: 0,
        }}
      >
        <li>
          ✅ <strong>Save hours:</strong> Instantly grab all results instead of
          manual copy-pasting
        </li>
        <li>
          📊 <strong>Analyze trends:</strong> Identify ranking patterns,
          top-performing titles, and descriptions
        </li>
        <li>
          🔍 <strong>Competitor research:</strong> Track what’s working for
          other creators in your niche
        </li>
        <li>
          🚀 <strong>SEO optimization:</strong> Discover keyword-rich titles &
          descriptions to rank higher
        </li>
        <li>
          📁 <strong>Multiple formats:</strong> Export data as CSV, Excel, or
          JSON for easy analysis
        </li>
      </ul>

      {/* How It Works */}
      <Typography variant="h5" fontWeight="bold" gutterBottom mt={4}>
        How to Export YouTube Search Results in 3 Easy Steps
      </Typography>
      <Box
        sx={{
          backgroundColor: "#f5f5f5",
          p: 3,
          borderRadius: 2,
          textAlign: "left",
          display: "inline-block",
        }}
      >
        <ol>
          <li>
            <strong>Enter</strong> your search query or keyword
          </li>
          <li>
            <strong>Select</strong> country for localized results
          </li>
          <li>
            <strong>Click</strong> "Export Search Results" and get your data
            instantly
          </li>
        </ol>
      </Box>

      {/* Social Proof */}
      <Typography variant="h5" fontWeight="bold" gutterBottom mt={4}>
        Trusted by Researchers & Marketers
      </Typography>
      <Box sx={{ fontStyle: "italic", mb: 3 }}>
        <Typography paragraph>
          "This tool saved me hours of manual work! Now I can analyze trends
          effortlessly and optimize my content strategy."
        </Typography>
        <Typography>- Mark T., Digital Marketing Analyst</Typography>
      </Box>

      {/* FAQs */}
      <Typography variant="h5" fontWeight="bold" gutterBottom>
        Frequently Asked Questions
      </Typography>

      <Box textAlign="left" sx={{ display: "inline-block" }}>
        <Typography variant="body1" fontWeight="bold">
          📌 What data is included in the export?
        </Typography>
        <Typography variant="body2" paragraph sx={{ mb: 2 }}>
          The export includes video titles, descriptions, URLs, view counts,
          upload dates, and channel names.
        </Typography>

        <Typography variant="body1" fontWeight="bold">
          📁 What file formats can I export to?
        </Typography>
        <Typography variant="body2" paragraph sx={{ mb: 2 }}>
          You can download the data as CSV, Excel, or JSON, depending on your
          needs.
        </Typography>

        <Typography variant="body1" fontWeight="bold">
          ⏱️ How long does it take?
        </Typography>
        <Typography variant="body2" paragraph sx={{ mb: 2 }}>
          Less than 10 seconds! Your results are processed instantly.
        </Typography>

        <Typography variant="body1" fontWeight="bold">
          🔍 How to get better search results on YouTube?
        </Typography>
        <Typography variant="body2" paragraph sx={{ mb: 2 }}>
          By analyzing exported data from real search results, our tool helps
          you discover which keywords, titles, and content formats perform best
          — enabling smarter optimization decisions.
        </Typography>

        <Typography variant="body1" fontWeight="bold">
          🎯 How to get your YouTube video in search results?
        </Typography>
        <Typography variant="body2" paragraph sx={{ mb: 2 }}>
          Use the insights from our tool to understand what types of content and
          keywords rank for your niche, then apply those learnings to your video
          metadata and strategy.
        </Typography>

        <Typography variant="body1" fontWeight="bold">
          🌍 How to get YouTube search results from a specific country?
        </Typography>
        <Typography variant="body2" paragraph sx={{ mb: 2 }}>
          Our tool lets you choose from multiple countries so you can see what
          ranks in localized search results and tailor your content accordingly.
        </Typography>

        <Typography variant="body1" fontWeight="bold">
          📈 How to get better YouTube search results?
        </Typography>
        <Typography variant="body2" paragraph sx={{ mb: 2 }}>
          Instead of guessing, extract and study top-performing videos to
          fine-tune your own strategy for better visibility and rankings in
          YouTube search.
        </Typography>
      </Box>

      {/* CTA Section */}
      <Box mt={4} mb={6}>
        <Typography variant="h6" paragraph sx={{ fontWeight: "bold" }}>
          Ready to extract YouTube search results effortlessly?
        </Typography>
        <Button
          variant="contained"
          color="primary"
          size="large"
          onClick={scrollToTop}
          sx={{
            fontSize: "1.1rem",
            py: 1.5,
            px: 4,
            fontWeight: "bold",
          }}
        >
          Export My YouTube Search Results Now →
        </Button>
        <Typography variant="caption" display="block" mt={1}>
          <b>1 credit = 1 Full search results export</b>
        </Typography>
      </Box>
    </Container>
  );
};

export default YouTubeSearchResultsExportContent;
